import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Avatar,
  Button,
  CssBaseline,
  TextField,
  Link,
  Grid,
  Box,
  Container,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  FormHelperText,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import PublicLayout from "layouts/publicLayout";
import Copyright from "components/copyright";
import { red } from "@mui/material/colors";
import axios from "axios";
import ErrorAlert from "helper/errorAlert";
import { useApiClient } from "context/ApiClientContext";
import logo from "../../assets/images/gosum.png"

const defaultTheme = createTheme();

export default function Signin() {
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [countdown, setCountdown] = useState(60); // 60 seconds countdown
  const [isCountdownActive, setIsCountdownActive] = useState(false);
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const apiClient = useApiClient();

  const handleClose = () => {
    setShowCodeInput(false);
    setCountdown(60);
    setIsCountdownActive(false);
  };

   // Track if the token has already been fetched
   const tokenFetched = useRef(false);

   const getToken = useCallback(async () => {
     try {
       const response = await axios.get(`${apiClient}/token/csrf-token`, {
         headers: {
           Accept: "application/json",
           "Content-Type": "application/json",
         },
       });
 
       const csrfToken = response.data.csrfToken;
 
       // Only update the state and set the token if it hasn't been fetched yet
       if (!tokenFetched.current) {
         setData((prevData) => ({
           ...prevData,
           token: csrfToken,
         }));
 
         // Mark the token as fetched
         tokenFetched.current = true;
       }
     } catch (error) {
       ErrorAlert(error, navigate);
       console.error("An error occurred:", error.response);
     }
   }, [apiClient, navigate]);
 
   useEffect(() => {
     let timer;
 
     // Call getToken only once initially
     if (!tokenFetched.current) {
       getToken();
     }
 
     if (isCountdownActive) {
       timer = setInterval(() => {
         setCountdown((prev) => {
           if (prev > 1) {
             return prev - 1;
           } else {
             clearInterval(timer);
             setIsCountdownActive(false);
             return 0;
           }
         });
       }, 1000);
     }
 
     return () => clearInterval(timer);
   }, [isCountdownActive, getToken]);

  const validate = (name, value) => {
    switch (name) {
      case "email":
        if (!value.trim()) return "Email is required";
        return "";

      case "password":
        if (!value) return "Password is required";
        return "";

      case "code":
        if (!value) return "Code is required";
        return "";

      default:
        return "";
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleChange = (property, event) => {
    const dataCopy = { ...data };
    dataCopy[property] = event.target.value;
    setData(dataCopy);

    const error = validate(property, event.target.value);
    setErrors({ ...errors, [property]: error });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!data.email) newErrors.email = "Email is required";
    if (!data.password) newErrors.password = "Password is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Returns true if no errors
  };


  const validateCode = () => {
    const newErrors = {};
    if (!data.code) newErrors.code = "Code is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Returns true if no errors
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!showCodeInput) {
      if (validateForm()) {
        try {
          setOpenBackdrop(true);
          await new Promise((resolve) => setTimeout(resolve, 3000));

          const result = await axios.post(`${apiClient}/user/login`, data);

          const token = result?.data?.token;

          localStorage.setItem("token", token);
          localStorage.setItem("loggedInID", result?.data?.id);
          localStorage.setItem("companyId", result?.data?.lastcompany || 1);
          localStorage.setItem("loggedInUsername", result?.data?.userName || "");
          localStorage.setItem("roleId", result?.data?.roleid || "");
          localStorage.setItem("activeTab", "0");
          setOpenBackdrop(false);

          localStorage.setItem("2FA_PENDING", result?.data?.twoFA);

          if (result?.data?.twoFA) {
            setShowCodeInput(true);
            setIsCountdownActive(true); // Start countdown
          } else {
            navigate("/");
          }
        } catch (error) {
          ErrorAlert(error);
          setTimeout(() => {
            window.location.reload();
          }, 3000);
        }
      }
    } else {
      if (validateCode()) {
        try {
          await axios.post(
            `${apiClient}/user/validate2fa`,
            { email: data.email, code: data.code },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                Accept: "application/json",
              },
            }
          );
          localStorage.setItem("2FA_PENDING", "false");
          navigate("/");

        } catch (error) {
          ErrorAlert(error, navigate);
          return error;
        }
      }

    }
  };

  const handleResendCode = async () => {
    const token = localStorage.getItem("token");

    setCountdown(60);
    setIsCountdownActive(true);
    try {
      await axios.post(`${apiClient}/user/resend2fa`, { email: data.email },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },

        }

      );
    } catch (error) {
      ErrorAlert(error);
      return error;
    }


  };
  return (
    <PublicLayout>
      <ThemeProvider theme={defaultTheme}>
        <Container component="main" maxWidth="xs" >
          <CssBaseline />
          <Box
            mt={10}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
                sx={{
                  width: 150,
                  height: 150,
                  borderRadius: '50%',
                  backgroundColor: '#161c5e', // Change to your preferred color
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img src={logo} width={80} alt="Logo" />
              </Box>

            <Box
              component="form"
              autoComplete="off"
              onSubmit={handleSubmit}

            >

              <TextField
                sx={{ display: "none" }}
                disabled
                margin="normal"
                fullWidth
                id="csrf"
                type="text"
                label="CSRF Token"
                name="csrf"
                value={data.token}
              />
              <TextField
                error={!!errors.email}
                helperText={errors.email}
                onChange={(event) => handleChange("email", event)}
                margin="normal"
                fullWidth
                id="email"
                type="email"
                label="Email"
                name="email"
              />

              <FormControl
                fullWidth
                variant="outlined"
                helperText={errors.password}
                error={!!errors.password}
              >
                <InputLabel htmlFor="password">Password</InputLabel>
                <OutlinedInput
                  id="password"
                  value={data.password}
                  type={showPassword ? "text" : "password"}
                  onChange={(event) => handleChange("password", event)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {!showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Password"
                />
                {errors.password && (
                  <FormHelperText
                    style={{ color: red[700], fontSize: "0.75rem" }}
                  >
                    {errors.password}
                  </FormHelperText>
                )}
              </FormControl>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                sx={{ my: 1 }}
              >
                Sign In
              </Button>

              {!showCodeInput && (
                <Grid container>
                  <Grid item xs>
                    <Link href="/forgotPassword" variant="body2">
                      Forgot password?
                    </Link>
                  </Grid>
                </Grid>
              )}
            </Box>
            <Copyright />
          </Box>
        </Container>
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={openBackdrop}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <Dialog
          sx={{ px: 4 }}
          open={showCodeInput}
          maxWidth="sm"
          fullWidth
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", px: 2 }}>
            <IconButton onClick={handleClose} color="inherit" sx={{ mt: 1 }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <IconButton
              onClick={() => setShowCodeInput(false)}
              color="inherit"
            ></IconButton>
            <LockOutlinedIcon sx={{ fontSize: 40 }} />
          </Box>
          <DialogTitle sx={{ textAlign: "center", mt: 1 }}>
            2-Step Verification
          </DialogTitle>
          <DialogContent sx={{ textAlign: "center" }}>
            <DialogContentText>
              We sent a verification code to your email.
            </DialogContentText>
            <DialogContentText>
              Please enter the code in the field below.
            </DialogContentText>
            <Box sx={{ width: "100%" }}>
              <TextField
                value={data.code || ""}
                error={!!errors.code}
                helperText={errors.code}
                onChange={(event) => handleChange("code", event)}
                margin="normal"
                fullWidth
                id="code"
                label="Verification Code"
                name="code"
              />
              <Box sx={{ textAlign: "center", mt: 1 }}>
                <Typography variant="body2" color="textSecondary">
                  Code expires in: {countdown} seconds
                </Typography>
              </Box>
              <DialogActions>
                <Button
                  onClick={handleSubmit}
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{ my: 1 }}
                  disabled={countdown === 0}
                >
                  Verify Code
                </Button>
              </DialogActions>
            </Box>
          </DialogContent>
          <DialogContent sx={{ textAlign: "center" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mt: 2,
              }}
            >
              <DialogContentText>Didn't receive the code?</DialogContentText>
              <Link
                href=""
                onClick={(e) => {
                  e.preventDefault();
                  handleResendCode();
                }}
                sx={{ marginLeft: 1 }}
                style={{ pointerEvents: isCountdownActive ? "none" : "auto", color: isCountdownActive ? "gray" : "blue", }}
              >
                Resend code
              </Link>
            </Box>
          </DialogContent>
        </Dialog>
      </ThemeProvider>
    </PublicLayout>
  );
}